import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../lib/auth';
import { agencies } from '../../data/agencies';
import { GET_BOOKINGS, GET_SENT_BOOKINGS } from '../../graphql/queries/booking';
import { GET_AGENCY } from '../../graphql/queries/agency';
import { CANCEL_BOOKING, DELETE_BOOKING } from '../../graphql/mutations/booking';
import { useMutation } from '@apollo/client';

const STATUS_OPTIONS = [
  { value: 'pending_availability', label: 'En attente de confirmation de la disponibilité' },
  { value: 'accepted_unpaid', label: 'Réservation acceptée - non payée' },
  { value: 'deposit_paid', label: 'Acompte payé' },
  { value: 'fully_paid', label: 'Montant total payé' },
  { value: 'paid_partner', label: 'Paiement effectué via un partenaire' },
  { value: 'paid_organizer', label: "Paiement encaissé par l'organisateur" },
  { value: 'cancelled', label: 'Annulée' },
];
const PAIEMENT_OPTIONS = [
  { value: 'paye', label: 'Payé' },
  { value: 'avance', label: 'Avance' },
  { value: 'non_paye', label: 'Pas payé' },
];
const TYPE_RESERVATION_OPTIONS = [
  { value: 'enligne', label: 'En ligne' },
  { value: 'agence', label: 'Agence' },
  { value: 'partenaire', label: 'Partenaire' },
];

function BookingStatusBadge({ status }: { status: string }) {
  let color = 'gray';
  let label = status;
  switch (status) {
    case 'pending_availability':
      color = 'yellow';
      label = 'En attente de confirmation';
      break;
    case 'accepted_unpaid':
      color = 'orange';
      label = 'Acceptée - non payée';
      break;
    case 'deposit_paid':
      color = 'blue';
      label = 'Acompte payé';
      break;
    case 'fully_paid':
      color = 'green';
      label = 'Montant total payé';
      break;
    case 'paid_partner':
      color = 'purple';
      label = 'Payé via partenaire';
      break;
    case 'paid_organizer':
      color = 'teal';
      label = "Payé à l'organisateur";
      break;
    case 'rejected':
      color = 'red';
      label = 'Refusée';
      break;
    case 'cancelled':
      color = 'red';
      label = 'Annulée';
      break;
    default:
      color = 'gray';
      label = status;
  }
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-${color}-100 text-${color}-800`}>
      {label}
    </span>
  );
}

export default function MyBookingsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer l'ID de l'agence de l'utilisateur
  const userAgencyId = useMemo(() => {
    if (user?.agency && typeof user.agency === 'object' && 'id' in user.agency) {
      return user.agency.id;
    }
    if (user?.agencyId) {
      return user.agencyId;
    }
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.agency?.id) {
          return parsed.agency.id;
        }
        if (parsed.agencyId) {
          return parsed.agencyId;
        }
      }
    } catch (e) {
      console.error('Error parsing user from localStorage', e);
    }
    return null;
  }, [user]);

  const storedUser = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, [user]);

  const agencyName = storedUser?.agencyName || user?.agencyName;
  const { data: agencyData } = useQuery(GET_AGENCY, {
    variables: { name: agencyName },
    skip: !agencyName || !!userAgencyId,
    onCompleted: (data) => {
      if (data?.agency?.id && storedUser) {
        const updatedUser = { ...storedUser, agencyId: data.agency.id, agency: { ...storedUser.agency, id: data.agency.id } };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    },
  });

  const finalAgencyId = useMemo(() => {
    if (userAgencyId) return userAgencyId;
    if (agencyData?.agency?.id) {
      return agencyData.agency.id;
    }
    return null;
  }, [userAgencyId, agencyData]);

  // Récupérer les réservations depuis la base de données (mes packages)
  const { data: bookingsData, loading: bookingsLoading, error: bookingsError, refetch: refetchBookings } = useQuery(GET_BOOKINGS, {
    variables: { agencyId: finalAgencyId },
    skip: !finalAgencyId,
    fetchPolicy: 'cache-and-network',
  });

  // Récupérer les réservations envoyées (vers d'autres agences)
  const { data: sentBookingsData, loading: sentBookingsLoading, error: sentBookingsError, refetch: refetchSentBookings } = useQuery(GET_SENT_BOOKINGS, {
    variables: { agencyId: finalAgencyId },
    skip: !finalAgencyId,
    fetchPolicy: 'cache-and-network',
  });

  const [cancelBooking] = useMutation(CANCEL_BOOKING, {
    refetchQueries: [
      { query: GET_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
  });

  const [deleteBooking] = useMutation(DELETE_BOOKING, {
    refetchQueries: [
      { query: GET_BOOKINGS, variables: { agencyId: finalAgencyId } },
    ],
  });

  useEffect(() => {
    if (location.pathname === '/admin/my-bookings' && finalAgencyId && refetchBookings) {
      refetchBookings();
    }
  }, [location.pathname, finalAgencyId, refetchBookings]);

  // Transformer les données de la base de données
  const dbBookings = useMemo(() => {
    if (!bookingsData?.bookings) return [];
    
    return bookingsData.bookings.map((booking: any) => {
      // Vérifier si c'est une réservation interne (même agence)
      const packageAgencyId = booking.package?.agency?.id;
      const clientAgencyId = booking.client?.agencyId;
      const isInternalBooking = packageAgencyId && clientAgencyId && String(packageAgencyId) === String(clientAgencyId) && String(packageAgencyId) === String(finalAgencyId);
      
      const commissionPercent = booking.package?.commission || 10;
      const commissionAmount = isInternalBooking ? 0 : (booking.totalPrice * commissionPercent) / 100;
      const platformCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const agencyCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const partnerCommission = (isInternalBooking || booking.typeReservation !== 'partenaire') ? 0 : commissionAmount;

      let status = booking.statutReservation || booking.status?.toLowerCase() || 'pending_availability';
      
      if (booking.statutDisponibilite === 'disponible') {
        if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) {
          status = 'fully_paid';
        } else if (booking.paiement === 'avance' || (booking.montantAvance && booking.montantAvance > 0)) {
          status = 'deposit_paid';
        } else {
          status = 'accepted_unpaid';
        }
      } else if (booking.statutDisponibilite === 'non_disponible') {
        if (booking.statutReservation === 'annulée' || (booking.status === 'CANCELLED' && booking.statutReservation !== 'refusée')) {
          status = 'cancelled';
        } else if (booking.statutReservation === 'refusée' || booking.status === 'REJECTED') {
          status = 'rejected';
        } else {
          status = 'rejected';
        }
      } else {
        if (status === 'en attente' || status === 'pending') {
          status = 'pending_availability';
        } else if (status === 'confirmée' || status === 'confirmed') {
          if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) {
            status = 'fully_paid';
          } else if (booking.paiement === 'avance' || (booking.montantAvance && booking.montantAvance > 0)) {
            status = 'deposit_paid';
          } else {
            status = 'accepted_unpaid';
          }
        }
      }

      return {
        id: String(booking.id),
        packageId: String(booking.packageId),
        packageTitle: booking.package?.title || '',
        agencyId: booking.package?.agency?.id || finalAgencyId,
        agencyName: booking.package?.agency?.name || '',
        fromAgencyId: booking.client?.agencyId ? String(booking.client.agencyId) : (booking.package?.agency?.id || finalAgencyId),
        fromAgencyName: booking.client?.agency?.name || booking.package?.agency?.name || '',
        userId: String(booking.clientId),
        status: status,
        statutDisponibilite: booking.statutDisponibilite || 'attente',
        paiement: booking.paiement || 'non_paye',
        montantAvance: booking.montantAvance || 0,
        typeReservation: String(booking.typeReservation || 'agence'),
        travelerCount: booking.travelerCount,
        totalPrice: Number(booking.totalPrice) || 0,
        commissions: {
          platform: platformCommission,
          agency: agencyCommission,
          partner: partnerCommission,
        },
        contactInfo: {
          firstName: booking.nom || booking.client?.firstName || '',
          lastName: booking.prenom || booking.client?.lastName || '',
          email: booking.mail || booking.client?.email || '',
          phone: booking.telephone || booking.client?.phone || '',
        },
        createdAt: booking.createdAt,
        updatedAt: booking.updatedAt,
      };
    });
  }, [bookingsData, finalAgencyId]);

  // Transformer les réservations envoyées
  const sentBookings = useMemo(() => {
    if (!sentBookingsData?.sentBookings) return [];
    
    return sentBookingsData.sentBookings.map((booking: any) => {
      // Vérifier si c'est une réservation interne (même agence)
      const packageAgencyId = booking.package?.agency?.id;
      const clientAgencyId = booking.client?.agencyId || finalAgencyId;
      const isInternalBooking = packageAgencyId && clientAgencyId && String(packageAgencyId) === String(clientAgencyId) && String(packageAgencyId) === String(finalAgencyId);
      
      const commissionPercent = booking.package?.commission || 10;
      const commissionAmount = isInternalBooking ? 0 : (booking.totalPrice * commissionPercent) / 100;
      const platformCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const agencyCommission = isInternalBooking ? 0 : commissionAmount * 0.5;
      const partnerCommission = (isInternalBooking || booking.typeReservation !== 'partenaire') ? 0 : commissionAmount;

      let status = 'pending_availability';
      if (booking.statutDisponibilite === 'disponible') {
        if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) {
          status = 'fully_paid';
        } else if (booking.paiement === 'avance' || (booking.montantAvance && booking.montantAvance > 0)) {
          status = 'deposit_paid';
        } else {
          status = 'accepted_unpaid';
        }
      } else if (booking.statutDisponibilite === 'non_disponible') {
        if (booking.statutReservation === 'annulée' || booking.status === 'CANCELLED') {
          status = 'cancelled';
        } else {
          status = 'rejected';
        }
      }

      return {
        id: String(booking.id),
        packageId: String(booking.packageId),
        packageTitle: booking.package?.title || '',
        agencyId: booking.package?.agency?.id || null, // L'agence destinataire (qui possède le package)
        agencyName: booking.package?.agency?.name || '',
        fromAgencyId: booking.client?.agencyId ? String(booking.client.agencyId) : String(finalAgencyId), // Mon agence (émettrice)
        fromAgencyName: booking.client?.agency?.name || '',
        userId: String(booking.clientId),
        status: status,
        statutDisponibilite: booking.statutDisponibilite || 'attente',
        paiement: booking.paiement || 'non_paye',
        montantAvance: booking.montantAvance || 0,
        typeReservation: String(booking.typeReservation || 'partenaire'),
        travelerCount: booking.travelerCount,
        totalPrice: Number(booking.totalPrice) || 0,
        commissions: {
          platform: platformCommission,
          agency: agencyCommission,
          partner: partnerCommission,
        },
        contactInfo: {
          firstName: booking.nom || booking.client?.firstName || '',
          lastName: booking.prenom || booking.client?.lastName || '',
          email: booking.mail || booking.client?.email || '',
          phone: booking.telephone || booking.client?.phone || '',
        },
        createdAt: booking.createdAt,
        updatedAt: booking.updatedAt,
      };
    });
  }, [sentBookingsData, finalAgencyId]);

  // Combiner les réservations de mes packages et les réservations envoyées acceptées
  const allBookings = useMemo(() => {
    const userAgencyIdStr = String(finalAgencyId || 1);
    
    // Réservations de mes packages (acceptées ou annulées)
    const myPackageBookings = dbBookings.filter((booking: any) => {
      const agencyId = String(booking.agencyId);
      if (booking.statutDisponibilite === 'disponible' || booking.status === 'cancelled') {
        return agencyId === userAgencyIdStr;
      }
      return false;
    });
    
    // Réservations envoyées qui ont été acceptées
    const acceptedSentBookings = sentBookings.filter((booking: any) => {
      return booking.statutDisponibilite === 'disponible' || booking.status === 'cancelled';
    });
    
    return [...myPackageBookings, ...acceptedSentBookings];
  }, [dbBookings, sentBookings, finalAgencyId]);

  // Filtrer les réservations selon le filtre de statut
  const filteredBookings = useMemo(() => {
    return allBookings.filter((booking: any) => {
      return statusFilter === 'all' || booking.status === statusFilter;
    });
  }, [allBookings, statusFilter]);

  const totals = useMemo(() => {
    return filteredBookings.reduce((acc: any, booking: any) => {
      const totalPrice = Number(booking.totalPrice) || 0;
      let commission = 0;
      let commissionReceived = 0;
      let platformCommission = 0;
      
      // Déterminer si c'est une réservation envoyée (package d'une autre agence)
      const isSentBooking = booking.agencyId && String(booking.agencyId) !== String(finalAgencyId);
      
      if (isSentBooking) {
        // Réservation envoyée (créée par un partenaire) : on reçoit une commission partenaire
        commissionReceived = Number(booking.commissions?.partner) || 0;
        // Commission plateforme = 0 pour les réservations envoyées (package créé par une autre agence)
        platformCommission = 0;
      } else {
        // Réservation de mes packages (créée par mon agence)
        // Commissions versées = commission partenaire si c'est une réservation partenaire
        commission = Number(booking.commissions?.partner) || 0;
        // Commissions reçues = 0 pour les réservations de mes packages (on ne compte que celles des partenaires)
        // Commission plateforme = commission plateforme de la réservation
        platformCommission = Number(booking.commissions?.platform) || 0;
      }
      
      // Montant total :
      // - Si créée par mon agence : totalPrice
      // - Si créée par un partenaire : commission reçue
      const bookingTotal = isSentBooking ? commissionReceived : totalPrice;
      
      return {
        totalBookings: acc.totalBookings + 1,
        totalRevenue: acc.totalRevenue + bookingTotal,
        totalCommission: acc.totalCommission + commission,
        // Commissions reçues : uniquement pour les packages créés par une autre agence
        totalCommissionReceived: (acc.totalCommissionReceived || 0) + commissionReceived,
        // Commission plateforme : uniquement pour les packages créés par mon agence
        totalPlatformCommission: (acc.totalPlatformCommission || 0) + platformCommission,
      };
    }, {
      totalBookings: 0,
      totalRevenue: 0,
      totalCommission: 0,
      totalCommissionReceived: 0,
      totalPlatformCommission: 0,
    });
  }, [filteredBookings, finalAgencyId]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Mes Réservations</h1>
        </div>

        {/* Filtre par statut */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filtrer par statut:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="all">Tous</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Résumé des commissions */}
        <div className="grid gap-6 grid-cols-5">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Total réservations
            </h3>
            <p className="text-2xl font-semibold">
              {totals.totalBookings}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Montant total
            </h3>
            <p className="text-2xl font-semibold">
              {(Number(totals.totalRevenue) || 0).toLocaleString('fr-FR')} DA
            </p>
            <p className="text-xs text-gray-400 mt-1">Montant + Commissions reçues</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Commissions versées
            </h3>
            <p className="text-2xl font-semibold">
              {(Number(totals.totalCommission) || 0).toLocaleString('fr-FR')} DA
            </p>
            <p className="text-xs text-gray-400 mt-1">Versées aux partenaires</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Commissions reçues
            </h3>
            <p className="text-2xl font-semibold">
              {(Number(totals.totalCommissionReceived) || 0).toLocaleString('fr-FR')} DA
            </p>
            <p className="text-xs text-gray-400 mt-1">Reçues des partenaires</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">
              Montant total net
            </h3>
            <p className="text-2xl font-semibold">
              {((Number(totals.totalRevenue) || 0) - (Number(totals.totalCommission) || 0) - (Number(totals.totalPlatformCommission) || 0)).toLocaleString('fr-FR')} DA
            </p>
            <p className="text-xs text-gray-400 mt-1">Total - Versées - Commission plateforme</p>
          </div>
        </div>

        {/* Gestion des erreurs */}
        {(bookingsError || sentBookingsError) && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{bookingsError?.message || sentBookingsError?.message}</p>
            <button
              onClick={() => {
                if (refetchBookings) refetchBookings();
                if (refetchSentBookings) refetchSentBookings();
              }}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Loading */}
        {(bookingsLoading || sentBookingsLoading) && (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Chargement des réservations...</div>
          </div>
        )}

        {/* Tableau des réservations */}
        {!bookingsLoading && !sentBookingsLoading && (
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qui a réservé</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package (Créateur)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant / Commission</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                        Aucune réservation trouvée.
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking: any) => {
                      return (
                        <tr key={booking.id}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{booking.id}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(() => {
                              // Déterminer qui a fait la réservation
                              const fromAgencyIdStr = booking.fromAgencyId ? String(booking.fromAgencyId) : null;
                              const userAgencyIdStr = String(finalAgencyId || '');
                              
                              // Si c'est mon agence qui a fait la réservation
                              if (fromAgencyIdStr && fromAgencyIdStr === userAgencyIdStr) {
                                return <div className="font-medium text-blue-600">Moi</div>;
                              }
                              
                              // Si c'est une agence partenaire
                              if (booking.fromAgencyName && booking.typeReservation === 'partenaire') {
                                return <div className="font-medium text-gray-900">{booking.fromAgencyName}</div>;
                              }
                              
                              // Si c'est un client direct (en ligne)
                              if (booking.typeReservation === 'enligne' || !booking.fromAgencyName) {
                                return <div className="font-medium text-gray-900">Client direct</div>;
                              }
                              
                              return <div className="font-medium text-gray-900">-</div>;
                            })()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="font-medium text-gray-900">{booking.packageTitle}</div>
                            <div className="text-xs text-gray-500 mt-1">Créé par: {booking.agencyName || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{booking.contactInfo.firstName} {booking.contactInfo.lastName}</div>
                            <div className="text-xs text-gray-500">{booking.contactInfo.email}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap"><BookingStatusBadge status={booking.status} /></td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(() => {
                              // Si c'est une réservation envoyée (package d'une autre agence), afficher la commission reçue
                              const isSentBooking = booking.agencyId && String(booking.agencyId) !== String(finalAgencyId);
                              if (isSentBooking) {
                                return (Number(booking.commissions?.partner) || 0).toLocaleString('fr-FR') + ' DA';
                              }
                              // Sinon, afficher le montant total
                              return (Number(booking.totalPrice) || 0).toLocaleString('fr-FR') + ' DA';
                            })()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(() => {
                              try {
                                if (!booking.createdAt) return 'N/A';
                                let date: Date;
                                if (typeof booking.createdAt === 'string') {
                                  date = new Date(booking.createdAt);
                                  if (isNaN(date.getTime())) {
                                    const timestamp = parseInt(booking.createdAt);
                                    if (!isNaN(timestamp)) {
                                      date = new Date(timestamp);
                                    } else {
                                      date = new Date(booking.createdAt.replace(' ', 'T'));
                                    }
                                  }
                                } else if (booking.createdAt instanceof Date) {
                                  date = booking.createdAt;
                                } else {
                                  return 'N/A';
                                }
                                if (isNaN(date.getTime())) {
                                  return typeof booking.createdAt === 'string' 
                                    ? booking.createdAt.split('T')[0] 
                                    : 'N/A';
                                }
                                return format(date, 'dd MMM yyyy', { locale: fr });
                              } catch (error) {
                                if (typeof booking.createdAt === 'string') {
                                  const parts = booking.createdAt.split('T')[0].split('-');
                                  if (parts.length === 3) {
                                    return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                  }
                                }
                                return booking.createdAt || 'N/A';
                              }
                            })()}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setIsDetailsModalOpen(true);
                              }}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                              title="Voir les détails"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal de détails */}
        {isDetailsModalOpen && selectedBooking && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setIsDetailsModalOpen(false)}
              />
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Détails de la réservation #{selectedBooking.id}
                    </h3>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setIsDetailsModalOpen(false)}
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Informations générales</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Référence:</span> #{selectedBooking.id}</div>
                        <div><span className="font-medium">Package:</span> {selectedBooking.packageTitle}</div>
                        <div><span className="font-medium">Statut:</span> <BookingStatusBadge status={selectedBooking.status} /></div>
                        <div><span className="font-medium">Statut disponibilité:</span> {
                          selectedBooking.statutDisponibilite === 'disponible' ? 'Disponible' :
                          selectedBooking.statutDisponibilite === 'attente' ? 'En attente' :
                          'Non disponible'
                        }</div>
                        <div><span className="font-medium">Type réservation:</span> {
                          (() => {
                            const typeValue = String(selectedBooking.typeReservation || '');
                            const option = TYPE_RESERVATION_OPTIONS.find(opt => opt.value === typeValue);
                            return option?.label || typeValue;
                          })()
                        }</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Agence</h4>
                      <div className="space-y-2 text-sm">
                        {selectedBooking.typeReservation === 'partenaire' && selectedBooking.fromAgencyName ? (
                          <>
                            <div><span className="font-medium">Agence émettrice:</span> {selectedBooking.fromAgencyName}</div>
                            <div><span className="font-medium">Agence destinataire:</span> {selectedBooking.agencyName || 'Mon agence'}</div>
                          </>
                        ) : (
                          <>
                            <div><span className="font-medium">Agence:</span> {selectedBooking.agencyName || 'Mon agence'}</div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Client</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Nom:</span> {selectedBooking.contactInfo.firstName} {selectedBooking.contactInfo.lastName}</div>
                        <div><span className="font-medium">Email:</span> {selectedBooking.contactInfo.email}</div>
                        <div><span className="font-medium">Téléphone:</span> {selectedBooking.contactInfo.phone}</div>
                        <div><span className="font-medium">Nombre de voyageurs:</span> {selectedBooking.travelerCount}</div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Paiement</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Statut paiement:</span> {
                          PAIEMENT_OPTIONS.find(opt => opt.value === selectedBooking.paiement)?.label || selectedBooking.paiement
                        }</div>
                        <div><span className="font-medium">Montant avance:</span> {
                          selectedBooking.montantAvance ? `${selectedBooking.montantAvance.toLocaleString('fr-FR')} DA` : '-'
                        }</div>
                        <div><span className="font-medium">Montant total:</span> {
                          (Number(selectedBooking.totalPrice) || 0).toLocaleString('fr-FR')
                        } DA</div>
                      </div>
                    </div>
                    
                    {/* Afficher la section Commissions uniquement si ce n'est pas une réservation interne */}
                    {(() => {
                      const packageAgencyId = selectedBooking.agencyId;
                      const fromAgencyId = selectedBooking.fromAgencyId;
                      const isInternalBooking = packageAgencyId && fromAgencyId && 
                        String(packageAgencyId) === String(fromAgencyId) && 
                        String(packageAgencyId) === String(finalAgencyId);
                      
                      if (isInternalBooking) {
                        return null; // Masquer toute la section pour les réservations internes
                      }
                      
                      return (
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-2">Commissions</h4>
                          <div className="space-y-2 text-sm">
                            {/* Afficher la commission plateforme uniquement si le package est créé par mon agence */}
                            {selectedBooking.agencyId && String(selectedBooking.agencyId) === String(finalAgencyId) && (
                              <div><span className="font-medium">Commission plateforme:</span> {
                                (Number(selectedBooking.commissions?.platform) || 0).toLocaleString('fr-FR')
                              } DA</div>
                            )}
                            <div><span className="font-medium">Commission partenaire:</span> {
                              (Number(selectedBooking.commissions?.partner) || 0).toLocaleString('fr-FR')
                            } DA</div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Dates</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Date de création:</span> {
                          (() => {
                            try {
                              if (!selectedBooking.createdAt) return 'N/A';
                              let date: Date;
                              if (typeof selectedBooking.createdAt === 'string') {
                                date = new Date(selectedBooking.createdAt);
                                if (isNaN(date.getTime())) {
                                  const timestamp = parseInt(selectedBooking.createdAt);
                                  if (!isNaN(timestamp)) {
                                    date = new Date(timestamp);
                                  } else {
                                    date = new Date(selectedBooking.createdAt.replace(' ', 'T'));
                                  }
                                }
                              } else if (selectedBooking.createdAt instanceof Date) {
                                date = selectedBooking.createdAt;
                              } else {
                                return 'N/A';
                              }
                              if (isNaN(date.getTime())) {
                                return typeof selectedBooking.createdAt === 'string' 
                                  ? selectedBooking.createdAt.split('T')[0] 
                                  : 'N/A';
                              }
                              return format(date, 'dd MMM yyyy', { locale: fr });
                            } catch {
                              return 'N/A';
                            }
                          })()
                        }</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex gap-2 justify-end">
                    {selectedBooking.status !== 'cancelled' && selectedBooking.statutDisponibilite !== 'non_disponible' && (
                      <button
                        onClick={async () => {
                          if (confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) {
                            try {
                              await cancelBooking({
                                variables: {
                                  id: parseInt(selectedBooking.id, 10),
                                  annulationPar: 'agence',
                                },
                              });
                              alert('Réservation annulée avec succès');
                              setIsDetailsModalOpen(false);
                            } catch (error: any) {
                              alert('Erreur lors de l\'annulation : ' + error.message);
                            }
                          }
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        Annuler
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate(`/admin/bookings/edit/${selectedBooking.id}`);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={async () => {
                        if (confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) {
                          try {
                            await deleteBooking({ variables: { id: parseInt(selectedBooking.id) } });
                            alert('Réservation supprimée avec succès');
                            setIsDetailsModalOpen(false);
                          } catch (error: any) {
                            alert('Erreur lors de la suppression : ' + error.message);
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

